import React from 'react';
import { Link } from 'react-router-dom';
import { getPrivacyPolicyPageUrlByCountry } from './UrlUtils';
import {
    PAYMENT_MODE,
    ConsultantInvoicePaymentStatus,
    INVOICE_TRACKING_CORRECTION_DAYS,
} from '../invoicing/InvoicingConstants';
import {
    isWithinNDaysFromToday,
    addDaysToDate,
    ENGLISH_FORMAT,
} from './DateUtils';

export const consultationsWorldEmailLink = (
    <a
        href="mailto: consultations@world.deshaw.com"
        target="_blank"
        className={'text-blue-800'}
        rel="noreferrer"
    >
        {' '}
        Consultations (World){' '}
    </a>
);

export const icpInvoicingWorldEmailLink = (
    <a
        href="mailto: icp-invoicing@world.deshaw.com"
        target="_blank"
        className={'text-blue-800'}
        rel="noreferrer"
    >
        {' '}
        ICP Invoicing (World){' '}
    </a>
);

export const privacyPolicyLink = (country, label) => (
    <Link
        to={getPrivacyPolicyPageUrlByCountry(country)}
        target="_blank"
        className={'text-blue-800'}
        rel="noreferrer"
        data-testid={`privacy-notice-link-${country}`}
    >
        {label}
    </Link>
);

export const contactUsMessageElement = (
    <p>Please contact {consultationsWorldEmailLink} for any issue.</p>
);

export const getInvalidUrlMessageElement = (emailLink) => (
    <p>
        The link is no longer valid. Please contact
        {emailLink} to request for a new link.
    </p>
);

export const onboardingDataRecordedMessageElement = (
    <p>
        Your details are with us. If you want to alter/change your details,
        please email {consultationsWorldEmailLink} for any queries.{' '}
    </p>
);

export const retentionResponseReceivedMessageElement = (
    <p>
        Thank you for submitting your response. Please contact us at{' '}
        {consultationsWorldEmailLink} if you have any questions or comments
        regarding the ICP.{' '}
    </p>
);

export const getInvoicingHeaderMessageElement = (paymentStatus) => {
    switch (paymentStatus) {
        case ConsultantInvoicePaymentStatus.UNDER_REVIEW:
        case ConsultantInvoicePaymentStatus.DETAILS_VERIFIED:
        case ConsultantInvoicePaymentStatus.PAYMENT_INITIATED:
            return 'Submission successful';

        case ConsultantInvoicePaymentStatus.PAYMENT_SUCCESSFUL:
        case ConsultantInvoicePaymentStatus.PAYMENT_FAILED:
            return paymentStatus;

        default:
            return 'No Payment status';
    }
};

export const getInvoicingDetailsHeaderSubMessageElement = (
    paymentMethod,
    paymentStatus,
    invoiceSubmissionDate
) => {
    switch (paymentStatus) {
        case ConsultantInvoicePaymentStatus.UNDER_REVIEW: {
            // Allow correction in invoice details only if we're within 2 days of invoice submission
            const withinCorrectionPeriod = isWithinNDaysFromToday(
                invoiceSubmissionDate,
                INVOICE_TRACKING_CORRECTION_DAYS
            );

            if (!withinCorrectionPeriod) {
                // After 2 days, show simplified message
                return (
                    <p>
                        Your details are with us. Please reach out to{' '}
                        {icpInvoicingWorldEmailLink} for any queries.
                    </p>
                );
            }

            // Calculate the last date to reach out (submission date + 2 days)
            const lastReachOutDate = addDaysToDate(
                invoiceSubmissionDate,
                INVOICE_TRACKING_CORRECTION_DAYS,
                ENGLISH_FORMAT
            );

            // Within 2 days, show correction period message
            return (
                <p>
                    Your details are with us. If you wish to make any
                    corrections/changes to the submitted invoice, please reach
                    out to {icpInvoicingWorldEmailLink} by{' '}
                    <b>{lastReachOutDate}</b> (within{' '}
                    {INVOICE_TRACKING_CORRECTION_DAYS} days of submission).
                    <br />
                </p>
            );
        }

        case ConsultantInvoicePaymentStatus.DETAILS_VERIFIED:
        case ConsultantInvoicePaymentStatus.PAYMENT_INITIATED:
            return (
                <p>
                    Please reach out to {icpInvoicingWorldEmailLink} for any
                    queries.
                </p>
            );

        case ConsultantInvoicePaymentStatus.PAYMENT_SUCCESSFUL: {
            const message = getPaymentETAMessage(paymentMethod);

            return (
                <p>
                    {message} Please reach out to {icpInvoicingWorldEmailLink}{' '}
                    for any queries.
                </p>
            );
        }

        case ConsultantInvoicePaymentStatus.PAYMENT_FAILED:
            return (
                <p>
                    The payment for your invoice could not be processed. Please
                    reach out to {icpInvoicingWorldEmailLink} for any queries.
                </p>
            );

        default:
            return (
                <p>
                    There&apos;s no payment status available for your invoice.
                    Please reach out to {icpInvoicingWorldEmailLink} for any
                    queries.
                </p>
            );
    }
};

const getPaymentETAMessage = (paymentMethod) => {
    switch (paymentMethod) {
        case PAYMENT_MODE.BANK_PAYMENT:
        case PAYMENT_MODE.WIRE_TRANSFER:
            return 'The payment should reflect in the provided bank account within 2-3 business days.';

        case PAYMENT_MODE.MAIL_CHEQUE:
            return 'It might take 5-7 business days for the check to get delivered to the provided address.';

        default:
            return '';
    }
};
